<template>
  <div class="upcoming-reservations-card shadow-lg border-0">
    <div class="card-body p-4">
      <h5 class="card-title mb-4 d-flex align-items-center">
        <font-awesome-icon :icon="icon || 'fa-calendar-check'" class="me-2 text-warning"/>
        {{ title }} <small class="text-muted ms-2">(dzisiaj)</small>
      </h5>

      <div v-if="todayReservations.length === 0" class="text-center py-5 text-muted">
        <font-awesome-icon icon="fa-solid fa-inbox" size="2x" class="mb-3 opacity-50"/>
        <p class="mb-0">Brak rezerwacji</p>
      </div>

      <div v-else class="accordion" id="todayReservationsAccordion">
            <!-- Future / Upcoming Reservations -->
        <div
            v-for="res in todayReservations"
            :key="res.Uuid"
            class="accordion-item border rounded mb-3 overflow-hidden shadow-sm"
        >
          <h2 class="accordion-header fs-6 fw-normal m-0" :id="'heading-today-' + res.Uuid">
            <div
                class="accordion-header-content py-3 px-4 d-flex align-items-center w-100 gap-3 cursor-pointer"
                @click="toggleRow(res.Uuid, $event)"
            >
              <!-- Content Area -->
              <div class="d-flex align-items-center flex-grow-1 gap-3">
                <!-- Status Badge -->
                <div
                    class="status-badge d-flex align-items-center justify-content-center p-2 rounded-3 flex-shrink-0"
                    :class="getStatusBadgeClass(res)"
                    :title="getStatusTitle(res)"
                >
                  <font-awesome-icon :icon="getStatusIcon(res)" class="fs-6"/>
                </div>

                <!-- Main Info: Name, DateTime with Weekday -->
                <div class="me-5">
                  <div class="fw-bold text-body-emphasis mb-1">
                    <font-awesome-icon :icon="getReservationIcon(res.Type)" class="me-2 text-warning"/>
                    {{ formatType(res.Type) || 'Rezerwacja' }}: {{ res.TypeName || '-' }}
                  </div>
                  <small class="text-muted">
                    <font-awesome-icon icon="fa-solid fa-calendar-day" class="me-1"/>
                    {{ formatDate(res.StartDate) }} ({{ getDayName(res.StartDate) }})
                  </small>
                </div>

                <div class="vr"></div>

                <div class="me-3">
                  <font-awesome-icon icon="fa-solid fa-clock" class="me-1 ms-2"/>
                  {{ formatTime(res.StartDate) }} - {{ formatTime(res.EndDate) }}
                </div>

                <div class="vr"></div>

                <!-- Client Info -->
                <div class="me-4">
                  <div class="fw-bold text-body-emphasis mb-1">
                    <font-awesome-icon icon="fa-hashtag" class="me-2 text-warning"/>
                    {{ res.Uuid || '-' }}
                  </div>
                  <div class="fw-bold text-body-emphasis mb-1">
                    <font-awesome-icon icon="fa-phone" class="me-2 text-warning"/>
                    {{ res.ReservedForPhone || res.ContactPhone || '-' }}
                  </div>
                </div>

                <div class="vr"></div>

                <!-- Payment Info -->
                <div class="me-3">
                  <div class="fw-bold text-body-emphasis mb-1">
                    <font-awesome-icon icon="fa-coins" class="me-2 text-warning"/>
                    {{ res.Amount || '-' }} {{ res.Currency || '-' }}
                  </div>
                  <div class="fw-bold text-body-emphasis mb-1">
                    <font-awesome-icon icon="fa fa-credit-card" class="me-2 text-warning"/>
                    <span v-if="isPaid(res)">Opłacona</span>
                    <span class="text-bg-danger px-1" v-else>Nieopłacona</span>
                  </div>
                </div>
              </div>

              <!-- Quick Action Buttons -->
              <div class="quick-actions-container d-flex gap-2 justify-content-end align-items-center flex-shrink-0">
                <button
                    v-if="!isConfirmed(res) && !isCancelled(res)"
                    type="button"
                    class="btn btn-sm btn-success px-3"
                    title="Zatwierdź rezerwację"
                    @click="$emit('confirm', res)"
                    :disabled="isProcessing"
                >
                  <font-awesome-icon icon="fa-solid fa-check"/>
                </button>
                <button
                    v-if="!isCancelled(res)"
                    type="button"
                    class="btn btn-sm btn-danger px-3"
                    title="Anuluj rezerwację"
                    @click="$emit('cancel', res)"
                    :disabled="isProcessing"
                >
                  <font-awesome-icon icon="fa-solid fa-ban"/>
                </button>
                <button
                    v-if="!isPaid(res) && !isCancelled(res)"
                    type="button"
                    class="btn btn-sm btn-info px-3"
                    title="Oznacz opłaconą rezerwację"
                    @click="$emit('paid', res)"
                    :disabled="isProcessing"
                >
                  <font-awesome-icon icon="fa-solid fa-coins"/>
                </button>
              </div>

              <!-- Chevron indicator for accordion -->
              <div class="ms-2 text-muted">
                <font-awesome-icon icon="fa-solid fa-chevron-down"/>
              </div>
            </div>
          </h2>

          <div
              :id="'collapse-today-' + res.Uuid"
              class="accordion-collapse collapse"
              :aria-labelledby="'heading-today-' + res.Uuid"
              data-bs-parent="#todayReservationsAccordion"
          >
            <div class="accordion-body bg-body-tertiary p-4">
              <!-- Reservation Details Grid -->
              <div class="row g-3 mb-4">
                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold">Status</div>
                  <div class="text-body-emphasis">
                    <span
                        class="badge"
                        :class="{
                          'bg-success': isConfirmed(res),
                          'bg-warning text-dark': !isConfirmed(res) && !isCancelled(res),
                          'bg-danger': isCancelled(res)
                        }"
                    >
                      <font-awesome-icon icon="fa fa-info-circle"/> {{ getStatusText(res) }}
                    </span>
                    <span
                        class="badge m-1"
                        :class="{
                          'bg-success': isPaid(res),
                          'bg-warning text-dark': !isPaid(res)
                        }"
                    >
                      <font-awesome-icon icon="fa fa-credit-card"/> {{ isPaid(res) ? 'Opłacona' : 'Nieopłacona' }}
                    </span>
                  </div>
                </div>

                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold">Typ</div>
                  <div class="text-body-emphasis">
                    <font-awesome-icon :icon="getReservationIcon(res.Type)" class="me-1 text-warning"/>
                    {{ formatType(res.Type) || 'Rezerwacja' }}: {{ res.TypeName || '-' }}
                  </div>
                </div>

                <!--                <div class="col-sm-6 col-md-4">-->
                <!--                  <div class="small text-muted text-uppercase fw-bold">Użytkownik</div>-->
                <!--                  <div class="text-body-emphasis">{{ res.UserName || res.Email || '-' }}</div>-->
                <!--                </div>-->

                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold">ID Rezerwacji</div>
                  <div class="text-body-emphasis font-monospace text-break small">{{ res.Uuid || '-' }}</div>
                </div>

                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold">Email</div>
                  <div class="text-body-emphasis text-break">{{ res.Email || '-' }}</div>
                </div>

                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold">Telefon</div>
                      <div v-if="formatUserType(res.ReservedFor) === 'Gość'">
                        <input
                            type="text"
                            class="form-control form-control-sm"
                            v-model="getEditForm(res).contact_phone"
                            placeholder="Numer telefonu"
                        />
                      </div>
                      <div v-else class="text-body-emphasis">{{ res.ReservedForPhone || res.ContactPhone || '-' }}</div>
                </div>

                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold">Użytkownik</div>
                  <div class="text-body-emphasis">{{ formatUserType(res.ReservedFor) }}</div>
                </div>

                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold">Kwota</div>
                  <div class="text-body-emphasis fw-bold text-success">
                    {{ res.Amount ?? 0 }} {{ res.Currency || '' }}
                  </div>
                </div>

                <div class="col-sm-6 col-md-4">
                  <div class="small text-muted text-uppercase fw-bold">Typ Płatności</div>
                  <div class="text-body-emphasis fw-bold text-success">
                    {{ formatPaymentMethod(res.Paid) || '-' }}
                  </div>
                </div>

<!--                <div class="col-sm-6 col-md-4">-->
<!--                  <div class="small text-muted text-uppercase fw-bold">Data rozpoczęcia</div>-->
<!--                  <div class="text-body-emphasis">{{ formatDateTime(res.StartDate) }}</div>-->
<!--                </div>-->

<!--                <div class="col-sm-6 col-md-4">-->
<!--                  <div class="small text-muted text-uppercase fw-bold">Data zakończenia</div>-->
<!--                  <div class="text-body-emphasis">{{ formatDateTime(res.EndDate) }}</div>-->
<!--                </div>-->

                <div class="col-12 bg-body-secondary p-2 rounded">
                  <div class="small text-muted text-uppercase fw-bold mb-2">Notatka</div>
                  <textarea
                      class="form-control form-control-sm mb-2"
                      rows="3"
                      v-model="getEditForm(res).note"
                      placeholder="Wpisz notatkę..."
                  ></textarea>
                  <button
                      type="button"
                      class="btn btn-sm btn-primary"
                      @click="saveReservationDetails(res)"
                      :disabled="savingUuids.includes(res.Uuid)"
                  >
                    <span v-if="savingUuids.includes(res.Uuid)" class="spinner-border spinner-border-sm me-1"></span>
                    <font-awesome-icon v-else icon="fa-solid fa-floppy-disk" class="me-1" />
                    Zapisz
                  </button>
                </div>
              </div>

              <!-- Action Buttons at Bottom -->
              <hr class="my-4 opacity-25"/>
              <hr class="my-4 opacity-25"/>

              <div class="d-flex flex-wrap gap-2">
                <button
                    v-if="!isConfirmed(res) && !isCancelled(res)"
                    type="button"
                    class="btn btn-success"
                    @click="$emit('confirm', res)"
                    :disabled="isProcessing"
                >
                  <span v-if="isProcessing" class="spinner-border spinner-border-sm me-2"></span>
                  <font-awesome-icon icon="fa-solid fa-check" class="me-2"/>
                  Zatwierdź rezerwację
                </button>

                <button
                    v-if="isConfirmed(res) && !isCancelled(res)"
                    type="button"
                    class="btn btn-outline-warning"
                    @click="$emit('unconfirm', res)"
                    :disabled="isProcessing"
                >
                  <span v-if="isProcessing" class="spinner-border spinner-border-sm me-2"></span>
                  <font-awesome-icon icon="fa-solid fa-square-minus" class="me-2"/>
                  Cofnij potwierdzenie rezerwacji
                </button>

                <button
                    v-if="!isPaid(res) && !isCancelled(res)"
                    type="button"
                    class="btn btn-info"
                    @click="$emit('paid', res)"
                    :disabled="isProcessing"
                >
                  <span v-if="isProcessing" class="spinner-border spinner-border-sm me-2"></span>
                  <font-awesome-icon icon="fa-solid fa-coins" class="me-2"/>
                  Oznacz jako opłacona
                </button>

                <button
                    v-if="isPaid(res)"
                    type="button"
                    class="btn btn-outline-info"
                    @click="$emit('unpaid', res)"
                    :disabled="isProcessing"
                >
                  <span v-if="isProcessing" class="spinner-border spinner-border-sm me-2"></span>
                  <font-awesome-icon icon="fa-solid fa-coins" class="me-2"/>
                  Oznacz jako nieopłacona
                </button>

                <button
                    v-if="!isCancelled(res)"
                    type="button"
                    class="btn btn-danger"
                    @click="$emit('cancel', res)"
                    :disabled="isProcessing"
                >
                  <span v-if="isProcessing" class="spinner-border spinner-border-sm me-2"></span>
                  <font-awesome-icon icon="fa-solid fa-ban" class="me-2"/>
                  Anuluj rezerwację
                </button>

                <button
                    v-if="isCancelled(res)"
                    type="button"
                    class="btn btn-outline-danger"
                    @click="$emit('restore', res)"
                    :disabled="isProcessing"
                >
                  <span v-if="isProcessing" class="spinner-border spinner-border-sm me-2"></span>
                  <font-awesome-icon icon="fa-solid fa-reply" class="me-2"/>
                  Przywróć rezerwację
                </button>
              </div>

              <!-- Status Message -->
              <div v-if="isCancelled(res)" class="alert alert-danger mt-3 mb-0 py-2 small">
                <font-awesome-icon icon="fa-solid fa-ban" class="me-1"/>
                <strong>Anulowano:</strong> {{ formatDateTime(res.CanceledAt) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <FullPageAlert
      v-if="alertConfig.show"
      :title="alertConfig.title"
      :message="alertConfig.message"
      :is-confirm="alertConfig.isConfirm"
      :confirm-text="alertConfig.confirmText"
      :cancel-text="alertConfig.cancelText"
      :duration="alertConfig.duration"
      @close="alertConfig.show = false"
      @confirm="onAlertConfirm"
      @cancel="alertConfig.show = false"
  />
</template>

<script>
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import FullPageAlert from "@/components/notifications/FullPageAlert.vue";
    import {fetchWrapper} from "@/wrappers/api/fetchWrapper";
import * as bootstrap from 'bootstrap'

export default {
  name: 'ReservationsList',
  components: {FullPageAlert, FontAwesomeIcon},
      emits: ['confirm', 'unconfirm', 'paid', 'unpaid', 'cancel', 'restore', 'reservation-updated'],
  props: {
    reservations: {
      type: Array,
      default: () => []
    },
    cancelledReservations: {
      type: Array,
      default: () => []
    },
    title: String,
    icon: String,
  },
  data() {
    return {
      isProcessing: false,
          savingUuids: [],
          editForms: {},
      alertConfig: {
        show: false,
        title: '',
        message: '',
        isConfirm: false,
        confirmText: 'Potwierdź',
        cancelText: 'Anuluj',
        duration: 0,
        onConfirmAction: null
      },
      expandedReservation: null
    }
  },
  computed: {
    todayReservations() {
      const today = new Date().toISOString().split('T')[0]
      return (this.reservations || [])
          .filter(res => res.StartDate && res.StartDate.startsWith(today))
          .sort((a, b) => new Date(a.StartDate) - new Date(b.StartDate))
    }
  },
  methods: {
        getEditForm(res) {
          if (!this.editForms[res.Uuid]) {
            this.editForms[res.Uuid] = {
              contact_phone: res.ContactPhone || res.ReservedForPhone || '',
              note: res.Note || ''
            };
          }
          return this.editForms[res.Uuid];
        },
        async saveReservationDetails(res) {
          const form = this.getEditForm(res);
          this.savingUuids.push(res.Uuid);

          const payload = {
            uuid: res.Uuid,
            contact_phone: form.contact_phone || '',
            note: form.note || ''
          };

          try {
            await fetchWrapper.post('/admin/reservations/space/update', payload);
            this.$emit('reservation-updated');
          } catch (err) {
            this.showAlert({
              title: 'Błąd',
              message: typeof err === 'string' ? err : (err.message || 'Nie udało się zapisać zmian')
            });
          } finally {
            this.savingUuids = this.savingUuids.filter(id => id !== res.Uuid);
          }
        },
    isConfirmed(reservation) {
      return this.isValidDate(reservation?.ConfirmedAt)
    },
    isCancelled(reservation) {
      return this.isValidDate(reservation?.CanceledAt)
    },
    isPaid(reservation) {
      return this.isValidDate(reservation?.PaidAt)
    },
    isValidDate(dateStr) {
      if (!dateStr || dateStr.startsWith('0001-01-01')) return false
      const d = new Date(dateStr)
      return !isNaN(d.getTime()) && d.getFullYear() > 1
    },
    getStatusIcon(res) {
      if (this.isCancelled(res)) return 'fa-solid fa-circle-xmark'
      if (this.isConfirmed(res)) return 'fa-solid fa-circle-check'
      return 'fa-solid fa-hourglass-start'
    },
    getStatusBadgeClass(res) {
      if (this.isCancelled(res)) return 'bg-danger-subtle text-danger'
      if (this.isConfirmed(res)) return 'bg-success-subtle text-success'
      return 'bg-warning-subtle text-warning'
    },
    getStatusTitle(res) {
      if (this.isCancelled(res)) return 'Anulowano'
      if (this.isConfirmed(res)) return 'Zatwierdzone'
      return 'Niezatwierdzona'
    },
    getStatusText(res) {
      if (this.isCancelled(res)) return 'Anulowana'
      if (this.isConfirmed(res)) return 'Zatwierdzona'
      return 'Niezatwierdzona'
    },
    getReservationIcon(typeStr) {
      return (typeStr && typeStr.toLowerCase() === 'event') ? 'fa-solid fa-bullhorn' : 'fa-solid fa-bullseye'
    },
    formatType(typeStr) {
      return (typeStr && typeStr.toLowerCase() === 'event') ? 'Wydarzenie' : 'Stanowisko'
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pl-PL')
    },
    formatTime(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return isNaN(d.getTime()) ? '-' : d.toLocaleTimeString('pl-PL', {hour: '2-digit', minute: '2-digit'})
    },
    formatUserType(reservedFor) {
      if (reservedFor === null || reservedFor === undefined || reservedFor === 0) return 'Gość';
      return 'Zarejestrowany';
    },
    formatPaymentMethod(method) {
      if (method === null || method === undefined || method === '') return '-';
      const translations = {
        'pay_at_location': 'Płatność na miejscu',
        'p24': 'Przelewy24 (Online)'
      };
      return translations[method] || method;
    },
    getDayName(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pl-PL', {weekday: 'long'})
    },
    formatDateTime(dateStr) {
      if (!dateStr || !this.isValidDate(dateStr)) return '-'
      const d = new Date(dateStr)
      return `${d.toLocaleDateString('pl-PL')} ${d.toLocaleTimeString('pl-PL', {hour: '2-digit', minute: '2-digit'})}`
    },
    toggleRow(index, event) {
      if (event.target.closest('.quick-actions-container')) {
        return
      }
      const element = document.getElementById(`collapse-today-${index}`)
      if (element) {
        const collapseInstance = bootstrap.Collapse.getOrCreateInstance(element)
        collapseInstance.toggle()
      }
    },
    toggleReservation(uuid) {
      this.expandedReservation =
          this.expandedReservation === uuid
              ? null
              : uuid
    },
    showAlert({title, message, isConfirm = false, confirmText = 'Potwierdź', cancelText = 'Anuluj', duration = 0, onConfirm = null}) {
      this.alertConfig = {
        show: true,
        title,
        message,
        isConfirm,
        confirmText,
        cancelText,
        duration,
        onConfirmAction: onConfirm
      }
    },
    onAlertConfirm() {
      this.alertConfig.show = false
      if (typeof this.alertConfig.onConfirmAction === 'function') {
        this.alertConfig.onConfirmAction()
      }
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.accordion-header-content {
  background-color: var(--bs-body-bg);
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.upcoming-reservations-card {
  background-color: var(--bs-body-bg);
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  color: var(--bs-body-color);
}

.status-badge {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
}

.accordion-button:not(.collapsed) {
  background-color: rgba(240, 192, 64, 0.1);
  color: inherit;
  box-shadow: none;
}

.accordion-button:focus {
  box-shadow: none;
  border-color: rgba(240, 192, 64, 0.5);
}

.btn-success {
  background-color: #198754;
  border-color: #198754;
}

.btn-success:hover {
  background-color: #157347;
  border-color: #157347;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
}

.btn-danger:hover {
  background-color: #bb2d3b;
  border-color: #bb2d3b;
}

@media (max-width: 768px) {
  .accordion-button {
    flex-wrap: wrap;
  }

  .status-badge {
    margin-bottom: 0.5rem;
  }
}
</style>